/**
 * backtest_page_v2.js — DAS-105 Phase 03: Backtest 시안 매칭
 *
 * 매핑: BacktestDesktopV1 + BacktestMobileV1 (backtest.jsx)
 * 레이아웃 (시안 V1):
 *   - 데스크톱: 상단 2-col(1.3fr 1fr) — [수집기 3-col grid / 에러 통계 KPI 3개 + 액션],
 *     하단 풀-와이드 로그 테이블(시간/레벨/거래소/심볼/메시지)
 *   - 모바일: 수집기 2-col grid(slice 4) + KPI 2-col(총 오류/에러율) + 로그 EventRow 스택
 *
 * 데이터 소스:
 *   - services/infraApi.js: fetchInfraStatus, fetchInfraErrors, fetchInfraErrorStats
 *
 * BE gap (DAS-65-G1): stalledSec / 처리량 / latency 시안 표기는 BE 미지원
 *   → 운영 BE 필드(active/last_active_at/latency_ms/checkpoint_keys)로 대체.
 * 서버 측 필터 (level/exchange/search) + autoRefresh 보존.
 * silver 토큰 전용. JetBrains Mono 유지. 하드코딩 색상(#hex, rgba) 금지.
 */

import React from 'react';
import NavBar from '../common/nav_bar';
import MaintenanceBanner from '../common/maintenance_banner';
import ScrollToTopButton from '../common/scroll_to_top_button';
import { fetchInfraStatus, fetchInfraErrors, fetchInfraErrorStats } from '../../services/infraApi';
import { requireLoginOrThrow } from '../../utils/auth';
import { getNavCollapsed, setNavCollapsed } from '../../utils/navState';
import { isMobileViewport } from '../../utils/responsive';
import { HexLogo, StatusDot, V2Icon, V2Badge, KpiLabel } from '../common/claude_primitives';

/* ── 헬퍼 ─────────────────────────────────────────────────── */
function formatRelativeTime(isoString) {
    if (!isoString) return '—';
    const diff = Math.floor((Date.now() - new Date(isoString).getTime()) / 1000);
    if (diff < 10) return '방금 전';
    if (diff < 60) return `${diff}초 전`;
    if (diff < 3600) return `${Math.floor(diff / 60)}분 전`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}시간 전`;
    return `${Math.floor(diff / 86400)}일 전`;
}

function formatUptime(seconds) {
    if (!seconds && seconds !== 0) return '—';
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = Math.floor(seconds % 60);
    if (h > 0) return `${h}h ${m}m ${s}s`;
    if (m > 0) return `${m}m ${s}s`;
    return `${s}s`;
}

function formatUptimeShort(seconds) {
    if (!seconds && seconds !== 0) return '—';
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    if (h > 0) return `${h}h ${m}m`;
    if (m > 0) return `${m}m`;
    return `${Math.floor(seconds)}s`;
}

/* ── 수집기 타일 (시안 V1::CollectorTile) ────────────────── */
function CollectorTile({ adapter, size = 'md' }) {
    const pad = size === 'sm' ? 12 : 16;
    const active = adapter.active;
    const latencyMs = adapter.latency_ms ?? null;
    const checkpoints = adapter.checkpoint_keys ?? 0;
    const lastActive = adapter.last_active_at;
    // 시안: stall tone은 active 여부로 매핑 (BE stalledSec 미지원)
    const stallTone = active ? 'gain' : 'loss';
    const latTone = latencyMs === null ? 'neutral'
        : latencyMs < 100 ? 'gain'
        : latencyMs < 300 ? 'warning'
        : 'loss';

    return (
        <div
            role="listitem"
            aria-label={`${adapter.name || adapter.id} 수집기 — ${active ? '정상' : '비활성'}`}
            style={{
                padding: pad,
                borderRadius: 12,
                background: 'var(--color-surface-card-alt)',
                border: '1px solid var(--color-surface-border)',
                display: 'flex', flexDirection: 'column', gap: 6,
            }}
        >
            {/* 헤더: 이름 + 상태 점 */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span style={{
                    fontSize: 13, fontWeight: 600,
                    fontFamily: 'JetBrains Mono, monospace',
                    color: 'var(--color-content)',
                    letterSpacing: '-0.005em',
                }}>
                    {adapter.name || adapter.id}
                </span>
                <StatusDot tone={stallTone} size={7} />
            </div>
            {/* Last 타임스탬프 */}
            <div style={{
                fontFamily: 'JetBrains Mono, monospace',
                fontSize: 11, fontWeight: 500,
                color: 'var(--color-content-secondary)',
                display: 'flex', alignItems: 'baseline', gap: 6,
            }}>
                <span style={{
                    color: 'var(--color-content-muted)', fontSize: 9,
                    letterSpacing: '0.08em', textTransform: 'uppercase',
                }}>
                    Last
                </span>
                <span>{formatRelativeTime(lastActive)}</span>
            </div>
            {/* 체크포인트 건수 */}
            <div style={{
                fontFamily: 'JetBrains Mono, monospace',
                fontSize: 10,
                color: 'var(--color-content-muted)',
                display: 'flex', alignItems: 'center', gap: 10,
            }}>
                <span>체크포인트 {checkpoints}건</span>
            </div>
            {/* 헬스 스트립: latency + stall */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 2 }}>
                {latencyMs !== null && (
                    <span style={{
                        fontFamily: 'JetBrains Mono, monospace',
                        fontSize: 10, fontWeight: 600,
                        color: `var(--color-${latTone})`,
                        display: 'inline-flex', alignItems: 'center', gap: 3,
                    }}>
                        <span style={{
                            width: 4, height: 4, borderRadius: '50%',
                            background: `var(--color-${latTone})`,
                            flexShrink: 0,
                        }} aria-hidden="true" />
                        {latencyMs}ms
                    </span>
                )}
                <span style={{
                    fontFamily: 'JetBrains Mono, monospace',
                    fontSize: 10, fontWeight: 500,
                    color: `var(--color-${stallTone})`,
                }}>
                    {active ? '정상' : '비활성'}
                </span>
            </div>
        </div>
    );
}

/* ── 로그 테이블 행 (데스크톱) ────────────────────────────── */
function LogTableRow({ log }) {
    const lv = (log.level || 'info').toLowerCase();
    const lvColor = lv === 'error' ? 'var(--color-loss)'
        : lv === 'warn' || lv === 'warning' ? 'var(--color-warning)'
        : 'var(--color-content-muted)';
    const ts = log.timestamp || log.created_at || '';
    const timeStr = ts ? ts.replace('T', ' ').slice(0, 19) : '—';

    return (
        <tr style={{ borderBottom: '1px solid var(--color-surface-border)' }}>
            <td style={{
                padding: '8px 4px',
                fontFamily: 'JetBrains Mono, monospace',
                fontSize: 11, color: 'var(--color-content-muted)',
                whiteSpace: 'nowrap',
            }}>
                {timeStr}
            </td>
            <td style={{ padding: '8px 4px' }}>
                <span style={{
                    fontFamily: 'JetBrains Mono, monospace',
                    fontSize: 10, fontWeight: 700,
                    color: lvColor,
                    textTransform: 'uppercase',
                }}>
                    {lv}
                </span>
            </td>
            <td style={{
                padding: '8px 4px',
                fontFamily: 'JetBrains Mono, monospace',
                fontSize: 11, fontWeight: 600,
                color: 'var(--color-content-secondary)',
            }}>
                {log.exchange || '—'}
            </td>
            <td style={{
                padding: '8px 4px',
                fontFamily: 'JetBrains Mono, monospace',
                fontSize: 11, fontWeight: 600,
                color: 'var(--color-content-secondary)',
            }}>
                {log.symbol || '—'}
            </td>
            <td style={{ padding: '8px 4px', fontSize: 12, color: 'var(--color-content-secondary)' }}>
                {log.message || log.msg || '—'}
            </td>
        </tr>
    );
}

/* ── 모바일 이벤트 행 ────────────────────────────────────── */
function EventRow({ log }) {
    const lv = (log.level || 'info').toLowerCase();
    const tone = lv === 'error' ? 'loss' : lv === 'warn' || lv === 'warning' ? 'warning' : 'gain';
    const ts = log.timestamp || log.created_at || '';
    const timeStr = ts ? ts.replace('T', ' ').slice(0, 19) : '—';

    return (
        <div style={{
            display: 'flex', gap: 10,
            padding: '10px 0',
            borderBottom: '1px solid var(--color-surface-border)',
            alignItems: 'flex-start',
        }}>
            <StatusDot tone={tone} size={7} />
            <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
                    <V2Badge tone={tone}>{lv}</V2Badge>
                    {log.symbol && log.symbol !== '-' && (
                        <span style={{
                            fontFamily: 'JetBrains Mono, monospace',
                            fontSize: 11, fontWeight: 600,
                            color: 'var(--color-content)',
                        }}>
                            {log.symbol}
                        </span>
                    )}
                </div>
                <div style={{ fontSize: 11, color: 'var(--color-content-secondary)' }}>
                    {log.message || log.msg || '—'}
                </div>
                <div style={{
                    fontFamily: 'JetBrains Mono, monospace',
                    fontSize: 10, color: 'var(--color-content-muted)', marginTop: 2,
                }}>
                    {timeStr}{log.exchange ? ` · ${log.exchange}` : ''}
                </div>
            </div>
        </div>
    );
}

/* ── BacktestPageV2 메인 클래스 컴포넌트 ─────────────────── */
class BacktestPageV2 extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            collapsed: getNavCollapsed(),
            isMobile: isMobileViewport(),
            infraStatus: null,
            infraLoading: true,
            infraError: null,
            errors: [],
            errorsLoading: true,
            errorStats: null,
            // 클라이언트 측 빠른 레벨 필터
            logFilter: 'all',
            // 서버 측 필터 (DAS-66 패턴)
            logLevel: '',
            logExchange: '',
            logSearch: '',
            allLogExchanges: [],
            autoRefresh: (() => {
                const stored = localStorage.getItem('backtest_v2_autoRefresh');
                return stored === null ? true : stored === 'true';
            })(),
        };
        this._infraInterval = null;
        this._errorsInterval = null;
        this._resizeListener = null;
        this._searchDebounce = null;
    }

    componentDidMount() {
        // eslint-disable-next-line no-restricted-syntax -- componentDidMount only
        requireLoginOrThrow();
        this.fetchInfra();
        this.fetchErrors();
        this.fetchErrorStats();
        this._infraInterval = setInterval(this.fetchInfra, 5000);
        if (this.state.autoRefresh) {
            this._errorsInterval = setInterval(() => {
                this.fetchErrors();
                this.fetchErrorStats();
            }, 15000);
        }
        this._resizeListener = () => this.setState({ isMobile: isMobileViewport() });
        window.addEventListener('resize', this._resizeListener);
    }

    componentWillUnmount() {
        if (this._infraInterval) clearInterval(this._infraInterval);
        if (this._errorsInterval) clearInterval(this._errorsInterval);
        if (this._resizeListener) window.removeEventListener('resize', this._resizeListener);
        if (this._searchDebounce) clearTimeout(this._searchDebounce);
    }

    fetchInfra = () => {
        fetchInfraStatus()
            .then(res => this.setState({ infraStatus: res?.data, infraLoading: false, infraError: null }))
            .catch(err => this.setState({ infraError: err?.message || '조회 실패', infraLoading: false }));
    };

    fetchErrors = () => {
        const { logLevel, logExchange, logSearch } = this.state;
        const params = { limit: 100 };
        if (logLevel) params.level = logLevel;
        if (logExchange) params.exchange = logExchange;
        if (logSearch) params.search = logSearch;

        fetchInfraErrors(params)
            .then(res => {
                const data = res?.data;
                const results = Array.isArray(data) ? data : (data?.results || data?.errors || []);
                // DAS-116: allLogExchanges 는 모든 fetch(필터 유무 무관)에서 본 거래소를 누적 union.
                // 기존엔 필터 미적용 fetch 에서만 replace 라 새 거래소가 안 보이거나(영구 캐시) 사라졌음.
                this.setState(prev => {
                    const merged = [...new Set([
                        ...(prev.allLogExchanges || []),
                        ...results.filter(e => e.exchange).map(e => e.exchange),
                    ])].sort();
                    return { errors: results, allLogExchanges: merged, errorsLoading: false };
                });
            })
            .catch(() => this.setState({ errorsLoading: false }));
    };

    fetchErrorStats = () => {
        fetchInfraErrorStats()
            .then(res => this.setState({ errorStats: res?.data }))
            .catch(() => {});
    };

    onLogFilterChange = (field, value) => {
        this.setState({ [field]: value, errorsLoading: true }, () => {
            if (field === 'logSearch') {
                if (this._searchDebounce) clearTimeout(this._searchDebounce);
                this._searchDebounce = setTimeout(this.fetchErrors, 300);
            } else {
                this.fetchErrors();
            }
        });
    };

    toggleAutoRefresh = () => {
        this.setState(prev => {
            const next = !prev.autoRefresh;
            localStorage.setItem('backtest_v2_autoRefresh', String(next));
            return { autoRefresh: next };
        }, () => {
            if (this.state.autoRefresh) {
                if (this._errorsInterval) clearInterval(this._errorsInterval);
                this._errorsInterval = setInterval(() => {
                    this.fetchErrors();
                    this.fetchErrorStats();
                }, 15000);
            } else {
                if (this._errorsInterval) {
                    clearInterval(this._errorsInterval);
                    this._errorsInterval = null;
                }
            }
        });
    };

    onCollapse = (collapsed) => {
        setNavCollapsed(collapsed);
        this.setState({ collapsed });
    };

    onRefresh = () => {
        this.setState({ infraLoading: true, errorsLoading: true });
        this.fetchInfra();
        this.fetchErrors();
        this.fetchErrorStats();
    };

    getFilteredLogs = () => {
        const { errors, logFilter } = this.state;
        if (logFilter === 'all') return errors;
        return errors.filter(e => {
            const lv = (e.level || 'info').toLowerCase();
            if (logFilter === 'warn') return lv === 'warn' || lv === 'warning';
            return lv === logFilter;
        });
    };

    /* ── 데스크톱: 시안 V1 BacktestDesktopV1 정합 ─────────────── */
    renderDesktop() {
        const {
            infraStatus, infraLoading, infraError,
            errorStats, logFilter,
            logLevel, logExchange, logSearch, allLogExchanges, autoRefresh,
        } = this.state;

        const adapters = infraStatus?.adapters || [];
        const activeCount = adapters.filter(a => a.active).length;
        const allOk = adapters.length > 0 && activeCount === adapters.length;
        const uptimeSec = infraStatus?.uptime_seconds ?? null;
        const uptimeStr = uptimeSec !== null ? formatUptime(uptimeSec) : (infraStatus?.uptime || '—');
        const uptimeShortStr = uptimeSec !== null ? formatUptimeShort(uptimeSec) : (infraStatus?.uptime || '—');
        const totalErrors = errorStats?.total_errors ?? 0;
        const byLevel = errorStats?.by_level || {};
        // 심각 에러율 — BE recent_rate 미지원 시 fallback
        const errorRate = errorStats?.recent_rate !== undefined
            ? errorStats.recent_rate
            : (totalErrors > 0 && byLevel.ERROR ? (byLevel.ERROR / totalErrors * 100) : 0);
        const errorRateStr = errorRate.toFixed(2);
        const filteredLogs = this.getFilteredLogs();

        const FILTER_BTNS = [
            { key: 'all', label: '전체' },
            { key: 'error', label: 'ERROR' },
            { key: 'warn', label: 'WARN' },
            { key: 'info', label: 'INFO' },
        ];

        return (
            <>
                {/* 상단 2-col: 수집기 그리드(1.3fr) + 에러 통계(1fr) — 시안 V1 */}
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: '1.3fr 1fr',
                    gap: 14,
                    marginBottom: 16,
                }}>
                    {/* 수집기 그리드 카드 */}
                    <div style={{
                        padding: 18,
                        borderRadius: 14,
                        background: 'var(--color-surface-card)',
                        border: '1px solid var(--color-surface-border)',
                    }}>
                        <div style={{
                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                            marginBottom: 14, gap: 8,
                        }}>
                            <span style={{
                                fontSize: 14, fontWeight: 600,
                                color: 'var(--color-content)',
                                display: 'inline-flex', alignItems: 'center', gap: 8,
                            }}>
                                수집기 상태
                                <StatusDot
                                    tone={allOk ? 'gain' : 'warning'}
                                    label={allOk ? '정상' : '확인'}
                                    size={7}
                                />
                            </span>
                            <span style={{
                                fontFamily: 'JetBrains Mono, monospace',
                                fontSize: 11, color: 'var(--color-content-muted)',
                            }}>
                                가동 시간: {uptimeStr} · 정상 {activeCount}/{adapters.length || 0}
                            </span>
                        </div>
                        {infraLoading ? (
                            <div style={{
                                padding: '24px 0', textAlign: 'center',
                                fontSize: 12, color: 'var(--color-content-muted)',
                            }}>
                                로딩 중...
                            </div>
                        ) : adapters.length > 0 ? (
                            <div
                                role="list"
                                aria-label="수집기 목록"
                                style={{
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(3, 1fr)',
                                    gap: 10,
                                }}
                            >
                                {adapters.map(a => (
                                    <CollectorTile key={a.name || a.id} adapter={a} />
                                ))}
                            </div>
                        ) : (
                            <div style={{
                                padding: '24px 0', textAlign: 'center',
                                fontSize: 12, color: 'var(--color-content-muted)',
                            }}>
                                어댑터 없음
                            </div>
                        )}
                    </div>

                    {/* 에러 통계 카드 — KPI 3개 + 액션 버튼 */}
                    <div style={{
                        padding: 18,
                        borderRadius: 14,
                        background: 'var(--color-surface-card)',
                        border: '1px solid var(--color-surface-border)',
                        display: 'flex', flexDirection: 'column',
                    }}>
                        <div style={{
                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                            marginBottom: 16,
                        }}>
                            <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--color-content)' }}>
                                에러 통계
                            </span>
                        </div>
                        {/* KPI 3-col */}
                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(3, 1fr)',
                            gap: 14,
                        }}>
                            <div>
                                <KpiLabel>총 오류</KpiLabel>
                                <div style={{
                                    fontFamily: 'JetBrains Mono, monospace',
                                    fontSize: 28, fontWeight: 600,
                                    color: 'var(--color-content)',
                                    letterSpacing: '-0.02em',
                                }}>
                                    {totalErrors.toLocaleString()}
                                </div>
                            </div>
                            <div>
                                <KpiLabel>심각 에러율</KpiLabel>
                                <div style={{
                                    fontFamily: 'JetBrains Mono, monospace',
                                    fontSize: 28, fontWeight: 600,
                                    color: 'var(--color-content)',
                                    letterSpacing: '-0.02em',
                                }}>
                                    {errorRateStr}
                                </div>
                            </div>
                            <div>
                                <KpiLabel>가동 시간</KpiLabel>
                                <div style={{
                                    fontFamily: 'JetBrains Mono, monospace',
                                    fontSize: 22, fontWeight: 600,
                                    color: 'var(--color-content)',
                                    letterSpacing: '-0.02em',
                                }}>
                                    {uptimeShortStr}
                                </div>
                            </div>
                        </div>
                        {/* 액션 버튼 row */}
                        <div style={{
                            display: 'flex', gap: 8,
                            marginTop: 'auto', paddingTop: 18,
                            borderTop: '1px solid var(--color-surface-border)',
                        }}>
                            <button
                                type="button"
                                onClick={this.onRefresh}
                                aria-label="재조회"
                                style={{
                                    padding: '5px 11px', borderRadius: 8,
                                    border: '1px solid var(--color-surface-border)',
                                    background: 'transparent',
                                    color: 'var(--color-content-secondary)',
                                    fontSize: 11, fontWeight: 600,
                                    fontFamily: 'JetBrains Mono, monospace',
                                    cursor: 'pointer',
                                    display: 'inline-flex', alignItems: 'center', gap: 5,
                                    minHeight: 28,
                                }}
                            >
                                <V2Icon name="refresh" size={11} aria-hidden="true" />
                                재시작
                            </button>
                            <button
                                type="button"
                                aria-label="수집기 설정"
                                style={{
                                    padding: '5px 11px', borderRadius: 8,
                                    border: '1px solid transparent',
                                    background: 'transparent',
                                    color: 'var(--color-content-muted)',
                                    fontSize: 11, fontWeight: 600,
                                    fontFamily: 'JetBrains Mono, monospace',
                                    cursor: 'pointer',
                                    minHeight: 28,
                                }}
                            >
                                {/* TODO(DAS-105): 수집기 설정 모달 연동 필요 */}
                                수집기 설정
                            </button>
                            {/* 에러 레벨 분포 배지 — BE 미지원 시 자동 비표시 */}
                            {(byLevel.ERROR !== undefined || byLevel.WARN !== undefined) && (
                                <div style={{ display: 'inline-flex', gap: 5, marginLeft: 'auto', alignItems: 'center' }}>
                                    {byLevel.ERROR !== undefined && (
                                        <V2Badge tone="loss">E {byLevel.ERROR}</V2Badge>
                                    )}
                                    {byLevel.WARN !== undefined && (
                                        <V2Badge tone="warning">W {byLevel.WARN}</V2Badge>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* 풀-와이드 로그 테이블 — 시안 V1 에러 로그 섹션 */}
                <div style={{
                    padding: 18,
                    borderRadius: 14,
                    background: 'var(--color-surface-card)',
                    border: '1px solid var(--color-surface-border)',
                }}>
                    {/* 헤더: 타이틀 + 필터 컨트롤 */}
                    <div style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        marginBottom: 12, gap: 8, flexWrap: 'wrap',
                    }}>
                        <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--color-content)' }}>
                            에러 로그
                        </span>
                        <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                            {/* 레벨 드롭다운 — 시안 pill 스타일 */}
                            <select
                                value={logLevel}
                                onChange={e => this.onLogFilterChange('logLevel', e.target.value)}
                                aria-label="레벨 필터"
                                style={{
                                    padding: '4px 10px', borderRadius: 999,
                                    border: '1px solid var(--color-surface-border)',
                                    background: 'transparent',
                                    color: 'var(--color-content-secondary)',
                                    fontSize: 11,
                                    fontFamily: 'JetBrains Mono, monospace',
                                    cursor: 'pointer',
                                }}
                            >
                                <option value="">전체 레벨</option>
                                <option value="ERROR">ERROR</option>
                                <option value="WARN">WARN</option>
                                <option value="INFO">INFO</option>
                            </select>
                            {/* 거래소 드롭다운 */}
                            <select
                                value={logExchange}
                                onChange={e => this.onLogFilterChange('logExchange', e.target.value)}
                                aria-label="거래소 필터"
                                style={{
                                    padding: '4px 10px', borderRadius: 999,
                                    border: '1px solid var(--color-surface-border)',
                                    background: 'transparent',
                                    color: 'var(--color-content-secondary)',
                                    fontSize: 11,
                                    fontFamily: 'JetBrains Mono, monospace',
                                    cursor: 'pointer',
                                }}
                            >
                                <option value="">전체 거래소</option>
                                {allLogExchanges.map(ex => (
                                    <option key={ex} value={ex}>{ex}</option>
                                ))}
                            </select>
                            {/* 메시지 검색 */}
                            <input
                                type="text"
                                placeholder="메시지 검색..."
                                value={logSearch}
                                onChange={e => this.onLogFilterChange('logSearch', e.target.value)}
                                aria-label="메시지 검색"
                                style={{
                                    width: 220, height: 30,
                                    padding: '0 10px', borderRadius: 8,
                                    border: '1px solid var(--color-surface-border)',
                                    background: 'var(--color-surface)',
                                    color: 'var(--color-content)',
                                    fontSize: 12, outline: 'none',
                                    fontFamily: 'JetBrains Mono, monospace',
                                }}
                            />
                            {/* 자동 갱신 토글 */}
                            <button
                                type="button"
                                onClick={this.toggleAutoRefresh}
                                aria-pressed={autoRefresh}
                                // 접근성 이름은 보이는 글자를 포함해야 한다 (WCAG 2.5.3 Label in Name) — 종전에는
                                // 보이는 글자가 '자동 갱신 중지됨'인데 이름은 '자동 갱신 시작'이라 어긋났다 (DAS-176)
                                aria-label={autoRefresh ? '자동 갱신 중 — 클릭하면 중지' : '자동 갱신 중지됨 — 클릭하면 시작'}
                                style={{
                                    padding: '5px 11px', borderRadius: 8,
                                    border: autoRefresh
                                        ? '1px solid color-mix(in oklch, var(--color-gain) 40%, var(--color-surface-border))'
                                        : '1px solid var(--color-surface-border)',
                                    background: autoRefresh ? 'var(--color-gain-subtle)' : 'transparent',
                                    color: autoRefresh ? 'var(--color-gain)' : 'var(--color-content-secondary)',
                                    fontSize: 11, fontWeight: 600,
                                    fontFamily: 'JetBrains Mono, monospace',
                                    cursor: 'pointer',
                                    display: 'inline-flex', alignItems: 'center', gap: 5,
                                    minHeight: 30,
                                }}
                            >
                                <V2Icon name="refresh" size={11} aria-hidden="true" />
                                {autoRefresh ? '자동 갱신 중' : '자동 갱신 중지됨'}
                            </button>
                        </div>
                    </div>

                    {/* 클라이언트 측 빠른 레벨 pill 필터 */}
                    <div role="group" aria-label="레벨 빠른 필터" style={{ display: 'flex', gap: 5, marginBottom: 10 }}>
                        {FILTER_BTNS.map(btn => (
                            <button
                                key={btn.key}
                                type="button"
                                onClick={() => this.setState({ logFilter: btn.key })}
                                aria-pressed={logFilter === btn.key}
                                style={{
                                    padding: '3px 9px', borderRadius: 999,
                                    border: '1px solid var(--color-surface-border)',
                                    background: logFilter === btn.key
                                        ? 'var(--color-surface-elevated)'
                                        : 'transparent',
                                    color: logFilter === btn.key
                                        ? 'var(--color-content)'
                                        : 'var(--color-content-muted)',
                                    fontSize: 10,
                                    fontFamily: 'JetBrains Mono, monospace',
                                    fontWeight: 600, cursor: 'pointer',
                                    minHeight: 24,
                                }}
                            >
                                {btn.label}
                            </button>
                        ))}
                    </div>

                    {/* 테이블 */}
                    {filteredLogs.length === 0 ? (
                        <div style={{
                            padding: '32px 0', textAlign: 'center',
                            fontSize: 12, color: 'var(--color-content-muted)',
                        }}>
                            로그 없음
                        </div>
                    ) : (
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead>
                                <tr>
                                    {['시간', '레벨', '거래소', '심볼', '메시지'].map(th => (
                                        <th key={th} style={{
                                            textAlign: 'left', padding: '0 4px 8px',
                                            fontSize: 10, fontWeight: 600,
                                            letterSpacing: '0.08em', textTransform: 'uppercase',
                                            color: 'var(--color-content-muted)',
                                            fontFamily: 'JetBrains Mono, monospace',
                                            borderBottom: '1px solid var(--color-surface-border)',
                                        }}>
                                            {th}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {filteredLogs.map((log, i) => (
                                    <LogTableRow key={log.id || i} log={log} />
                                ))}
                            </tbody>
                        </table>
                    )}

                    {/* 푸터: 건수 표시 */}
                    <div style={{
                        padding: '12px 4px 0',
                        display: 'flex', justifyContent: 'flex-end',
                    }}>
                        <span style={{
                            fontFamily: 'JetBrains Mono, monospace',
                            fontSize: 11, color: 'var(--color-content-muted)',
                        }}>
                            최근 100건 표시 중 · 총 {filteredLogs.length}건
                        </span>
                    </div>
                </div>

                {/* 연결 불가 배너 — infraError 시 표시 */}
                {infraError && !infraLoading && (
                    <div style={{
                        marginTop: 14, padding: 16, borderRadius: 14,
                        background: 'var(--color-loss-subtle)',
                        border: '1px solid color-mix(in oklch, var(--color-loss) 30%, var(--color-surface-border))',
                        display: 'flex', alignItems: 'center', gap: 10,
                    }}>
                        <StatusDot tone="loss" size={8} />
                        <span style={{ fontSize: 13, color: 'var(--color-loss)', fontWeight: 600 }}>
                            BacktestInfra에 연결할 수 없습니다
                        </span>
                        <span style={{ fontSize: 12, color: 'var(--color-content-muted)' }}>
                            {infraError}
                        </span>
                    </div>
                )}
            </>
        );
    }

    /* ── 모바일: 시안 V1 BacktestMobileV1 정합 ─────────────── */
    renderMobile() {
        const { infraStatus, errorStats, logFilter, autoRefresh } = this.state;

        const adapters = infraStatus?.adapters || [];
        const activeCount = adapters.filter(a => a.active).length;
        const allOk = adapters.length > 0 && activeCount === adapters.length;
        const totalErrors = errorStats?.total_errors ?? 0;
        const byLevel = errorStats?.by_level || {};
        const errorRate = errorStats?.recent_rate !== undefined
            ? errorStats.recent_rate
            : (totalErrors > 0 && byLevel.ERROR ? (byLevel.ERROR / totalErrors * 100) : 0);
        const filteredLogs = this.getFilteredLogs();

        return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {/* 수집기 카드 — 2-col grid slice(0,4) */}
                {adapters.length > 0 && (
                    <div style={{
                        padding: 14,
                        borderRadius: 14,
                        background: 'var(--color-surface-card)',
                        border: '1px solid var(--color-surface-border)',
                    }}>
                        <div style={{
                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                            marginBottom: 10,
                        }}>
                            <span style={{
                                fontSize: 13, fontWeight: 600, color: 'var(--color-content)',
                                display: 'inline-flex', alignItems: 'center', gap: 7,
                            }}>
                                수집기 상태
                                <StatusDot tone={allOk ? 'gain' : 'warning'} size={7} />
                            </span>
                            <span style={{
                                fontFamily: 'JetBrains Mono, monospace',
                                fontSize: 10, color: 'var(--color-content-muted)',
                            }}>
                                {activeCount}/{adapters.length}
                            </span>
                        </div>
                        <div
                            role="list"
                            aria-label="수집기 목록"
                            style={{
                                display: 'grid',
                                gridTemplateColumns: '1fr 1fr',
                                gap: 8,
                            }}
                        >
                            {adapters.slice(0, 4).map(a => (
                                <CollectorTile key={a.name || a.id} adapter={a} size="sm" />
                            ))}
                        </div>
                    </div>
                )}

                {/* KPI row — 총 오류 / 에러율 */}
                <div style={{ display: 'flex', gap: 10 }}>
                    <div style={{
                        flex: 1, padding: 14, borderRadius: 14,
                        background: 'var(--color-surface-card)',
                        border: '1px solid var(--color-surface-border)',
                    }}>
                        <KpiLabel>총 오류</KpiLabel>
                        <div style={{
                            fontFamily: 'JetBrains Mono, monospace',
                            fontSize: 22, fontWeight: 600,
                            color: 'var(--color-content)',
                        }}>
                            {totalErrors.toLocaleString()}
                        </div>
                    </div>
                    <div style={{
                        flex: 1, padding: 14, borderRadius: 14,
                        background: 'var(--color-surface-card)',
                        border: '1px solid var(--color-surface-border)',
                    }}>
                        <KpiLabel>에러율</KpiLabel>
                        <div style={{
                            fontFamily: 'JetBrains Mono, monospace',
                            fontSize: 22, fontWeight: 600,
                            color: 'var(--color-content)',
                        }}>
                            {errorRate.toFixed(2)}
                        </div>
                    </div>
                </div>

                {/* 에러 로그 — EventRow 스택 */}
                <div style={{
                    padding: 14,
                    borderRadius: 14,
                    background: 'var(--color-surface-card)',
                    border: '1px solid var(--color-surface-border)',
                }}>
                    <div style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        marginBottom: 10,
                    }}>
                        <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-content)' }}>
                            에러 로그
                        </span>
                        <button
                            type="button"
                            onClick={this.toggleAutoRefresh}
                            aria-pressed={autoRefresh}
                            // 접근성 이름은 보이는 글자를 포함해야 한다 (WCAG 2.5.3 Label in Name) — 종전에는
                                // 보이는 글자가 '자동 갱신 중지됨'인데 이름은 '자동 갱신 시작'이라 어긋났다 (DAS-176)
                                aria-label={autoRefresh ? '자동 갱신 중 — 클릭하면 중지' : '자동 갱신 중지됨 — 클릭하면 시작'}
                            style={{
                                padding: '4px 10px', borderRadius: 8,
                                border: autoRefresh
                                    ? '1px solid color-mix(in oklch, var(--color-gain) 40%, var(--color-surface-border))'
                                    : '1px solid var(--color-surface-border)',
                                background: autoRefresh ? 'var(--color-gain-subtle)' : 'transparent',
                                color: autoRefresh ? 'var(--color-gain)' : 'var(--color-content-secondary)',
                                fontSize: 10, fontWeight: 600,
                                fontFamily: 'JetBrains Mono, monospace',
                                cursor: 'pointer',
                                display: 'inline-flex', alignItems: 'center', gap: 4,
                                minHeight: 28,
                            }}
                        >
                            <V2Icon name="refresh" size={10} aria-hidden="true" />
                            {autoRefresh ? '자동' : '수동'}
                        </button>
                    </div>
                    {/* 레벨 pill 필터 */}
                    <div role="group" aria-label="레벨 빠른 필터" style={{ display: 'flex', gap: 5, marginBottom: 10 }}>
                        {[
                            { key: 'all', label: '전체' },
                            { key: 'error', label: 'E' },
                            { key: 'warn', label: 'W' },
                            { key: 'info', label: 'I' },
                        ].map(btn => (
                            <button
                                key={btn.key}
                                type="button"
                                onClick={() => this.setState({ logFilter: btn.key })}
                                aria-pressed={logFilter === btn.key}
                                style={{
                                    padding: '3px 9px', borderRadius: 999,
                                    border: '1px solid var(--color-surface-border)',
                                    background: logFilter === btn.key
                                        ? 'var(--color-surface-elevated)'
                                        : 'transparent',
                                    color: logFilter === btn.key
                                        ? 'var(--color-content)'
                                        : 'var(--color-content-muted)',
                                    fontSize: 10,
                                    fontFamily: 'JetBrains Mono, monospace',
                                    fontWeight: 600, cursor: 'pointer',
                                    minHeight: 24,
                                }}
                            >
                                {btn.label}
                            </button>
                        ))}
                    </div>
                    {/* 로그 목록 */}
                    {filteredLogs.length === 0 ? (
                        <div style={{
                            padding: '24px 0', textAlign: 'center',
                            fontSize: 12, color: 'var(--color-content-muted)',
                        }}>
                            로그 없음
                        </div>
                    ) : (
                        filteredLogs.slice(0, 20).map((log, i) => (
                            <EventRow key={log.id || i} log={log} />
                        ))
                    )}
                </div>
            </div>
        );
    }

    render() {
        const { collapsed, isMobile, infraLoading } = this.state;

        return (
            <div className="min-h-screen w-full bg-surface text-content sm:overflow-x-auto">
                <NavBar collapsed={collapsed} onCollapse={this.onCollapse} />
                <main
                    className={`min-w-0 w-full pt-12 pb-16 sm:pt-0 sm:pb-0 transition-[padding-left] duration-200 ${
                        collapsed ? 'sm:pl-[56px]' : 'sm:pl-[220px]'
                    }`}
                    style={{ marginTop: 'var(--maintenance-banner-h, 0px)' }}
                >
                    <MaintenanceBanner />
                    <div className="mx-3 my-3 min-w-0 sm:mx-6 sm:my-5">

                        {/* 페이지 헤더 — 시안 V1 TopBar(crumbs/title) */}
                        <div style={{
                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                            marginBottom: 20, flexWrap: 'wrap', gap: 10,
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                <HexLogo
                                    size={18}
                                    stroke="var(--color-content-secondary)"
                                    accent="var(--color-brand)"
                                />
                                <div>
                                    <div style={{ fontSize: 12, color: 'var(--color-content-secondary)', marginBottom: 2 }}>
                                        백테스트 · 수집 모니터링
                                    </div>
                                    <h1 style={{
                                        fontSize: 20, fontWeight: 700,
                                        color: 'var(--color-content)',
                                        letterSpacing: '-0.02em', margin: 0,
                                    }}>
                                        수집 모니터링
                                    </h1>
                                </div>
                            </div>
                            <button
                                type="button"
                                onClick={this.onRefresh}
                                aria-label="새로고침"
                                className="inline-flex items-center gap-1.5 rounded-lg border border-surface-border bg-surface-card px-3 py-1.5 text-xs font-semibold text-content-secondary hover:bg-surface-hover transition-colors"
                                style={{ minHeight: 32 }}
                            >
                                <V2Icon name="refresh" size={12} aria-hidden="true" />
                                <span className="hidden sm:inline">새로고침</span>
                            </button>
                        </div>

                        {/* 로딩 스켈레톤 */}
                        {infraLoading && (
                            <div style={{
                                padding: 20, borderRadius: 14,
                                background: 'var(--color-surface-card)',
                                border: '1px solid var(--color-surface-border)',
                                fontSize: 13, color: 'var(--color-content-secondary)',
                                marginBottom: 16,
                            }}>
                                파이프라인 상태를 불러오는 중...
                            </div>
                        )}

                        {/* 콘텐츠 영역 */}
                        {!infraLoading && (
                            isMobile ? this.renderMobile() : this.renderDesktop()
                        )}
                    </div>
                </main>
                <ScrollToTopButton />
            </div>
        );
    }
}

export default BacktestPageV2;
